// 1. React
import React, { useEffect, useState } from 'react';

// 2. Third-party
import { Modal, Form, Input, InputNumber, Select, Switch, Button, Space, Table, Checkbox, Upload, Typography, Tag, message } from 'antd';
import { PlusOutlined, MinusCircleOutlined, UploadOutlined, FolderOpenOutlined, FilePdfOutlined } from '@ant-design/icons';

// 3. Services
import { propertyHostService, attachment } from '@iso/services';
import { apiEndpoint } from '@iso/lib/helpers/endpoint';
import { authHeader } from '@iso/lib/helpers/authHeader';
import { visitorTypes } from '@iso/constants/visitorTypes';
import FileBrowser from '@iso/components/FileBrowser/FileBrowser';
import { getFileName } from '@iso/components/FileBrowser/fileUtils';
import { resolveStoredFileUrl } from '@iso/lib/helpers/schemaFormWidgets';

// 4. Constants
import {
  LIBRARY_FIELD_WIDGETS,
  WIDGETS_WITH_OPTIONS,
  WIDGETS_WITH_CONTENT,
  DEFAULT_WIDGET,
  slugifyKey,
  normalizeWidget,
  FIELD_CLEAR_OPTIONS,
  RULE_KINDS,
  parseRulesToState,
  buildRulesFromState,
  getAllowedRuleKinds,
  getAllowedRuleTypeOptions,
  widgetSupportsValidationRules,
  pruneRulesForWidget,
  VISIBILITY_OPERATORS,
  VISIBILITY_OPERATORS_WITH_VALUE,
} from '@iso/constants/libraryFieldWidgets';

const { Text } = Typography;
const NUMERIC_RULE_KINDS = ['min', 'max', 'len'];
const SUCCESS_CODE = '0000';

// "Visitor Types" field: a dropdown whose options are the visitor-type { value, label }
// pairs from the constant. The field stores `options: [{ value, label }]` (like List
// Hosts) — it shows the label and submits the value. The editor selects by value.
const VISITOR_TYPE_SELECT_OPTIONS = visitorTypes.map((t) => ({ value: t.value, label: t.label }));
const ALL_VISITOR_VALUES = visitorTypes.map((t) => t.value);
// Selected option objects (in constant order) for a set of chosen values.
const visitorOptionsFor = (values) =>
  visitorTypes.filter((t) => values.indexOf(t.value) !== -1).map((t) => ({ value: t.value, label: t.label }));
// The chosen values from saved `options` (objects, or legacy plain strings).
const visitorValuesFrom = (options) => (Array.isArray(options)
  ? options.map((o) => (o && typeof o === 'object' ? o.value : o)).filter((v) => v !== undefined && v !== null).map(String)
  : []);

// Configure a single schema field. onOk receives a plain field object:
// { key, label, widget?, required?, protected?, placeholder?, options?, clear?, rules?, hostOptions?, requiredVisitorTypes?, visibleWhen? }
// `propertyId` is set only in the per-property (standalone) builder — it enables the
// "List Hosts" (host-picker) config: pick which property hosts appear + their labels.
// `priorFields` are the fields that appear BEFORE this one (any earlier step, or an
// earlier field in the same step) — the candidates a visibility condition can depend on.
// `visitorTypeOptions` are the { value, label } pairs from the form's Visitor Types
// field (if any) — used by the List Hosts "Required for visitor types" config.
// `isProperty` marks the per-property (standalone) builder: the "Protected" switch
// belongs to the library template only, so it is hidden and never written there.
const FieldEditorModal = ({ visible, field, existingKeys, stepOptions, priorFields, visitorTypeOptions, propertyId, isProperty, onOk, onCancel }) => {
  const [form] = Form.useForm();
  const [widget, setWidget] = useState(DEFAULT_WIDGET);
  const [keyTouched, setKeyTouched] = useState(false);
  const [rules, setRules] = useState([]);
  // List Hosts config: the property's admin/owner users (Manage Hosts data) and,
  // per user_id, whether it is selected for this field + its (editable) label.
  const [hostRows, setHostRows] = useState([]);
  const [hostSelected, setHostSelected] = useState({}); // user_id -> bool
  const [hostLabels, setHostLabels] = useState({}); // user_id -> label
  const [hostLoading, setHostLoading] = useState(false);
  // Conditional visibility: null = always visible; otherwise { field, operator, value }.
  const [cond, setCond] = useState(null);
  // "Visitor Types" field: the selected visitor-type values (built into option objects on save).
  const [visitorValues, setVisitorValues] = useState([]);
  // Answer key for a graded question. Only choice widgets can carry one: there
  // is a fixed list of options to mark against, whereas auto-grading free text
  // would be a guessing game dressed up as a score.
  const [scoringCorrect, setScoringCorrect] = useState([]);
  const [scoringPoints, setScoringPoints] = useState(1);
  // Fixed PDF: the File Manager picker, which carries its own Upload tab — so
  // one control covers both "from this computer" and "from the File Manager"
  // rather than bolting a second uploader onto the side of it.
  const [pdfBrowserVisible, setPdfBrowserVisible] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  // "List Hosts" field: visitor-type values for which host selection is required
  // (drives meta_data.required_visitor_types_for_hosts for the mobile app).
  const [hostRequiredTypes, setHostRequiredTypes] = useState([]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    if (field) {
      const w = normalizeWidget(field.widget, !!(field.options && field.options.length));
      setWidget(w);
      setKeyTouched(true);
      setRules(parseRulesToState(field.rules));
      setCond(field.visibleWhen && field.visibleWhen.field ? { ...field.visibleWhen } : null);
      if (field.widget === 'visitor-types') {
        const vals = visitorValuesFrom(field.options);
        setVisitorValues(vals.length ? vals : [...ALL_VISITOR_VALUES]);
      } else {
        setVisitorValues([]);
      }
      const seedScoring = field.scoring || null;
      setScoringCorrect(seedScoring && Array.isArray(seedScoring.correct) ? seedScoring.correct.map(String) : []);
      setScoringPoints(seedScoring && seedScoring.points ? seedScoring.points : 1);
      setHostRequiredTypes(Array.isArray(field.requiredVisitorTypes) ? field.requiredVisitorTypes.map(String) : []);
      form.setFieldsValue({
        label: field.label,
        key: field.key,
        widget: w,
        required: !!field.required,
        protected: !!field.protected,
        placeholder: field.placeholder,
        // Visitor Types keeps its selection in visitorValues (its options are
        // { value, label } objects), so the generic string-options field stays empty.
        options: field.widget === 'visitor-types' ? [] : (field.options || []),
        clear: field.clear || '',
        hiddenId: field.hiddenId,
        content: field.content,
        contentName: field.contentName,
        max: field.max,
        dividerAfter: !!field.dividerAfter,
      });
    } else {
      setWidget(DEFAULT_WIDGET);
      setKeyTouched(false);
      setRules([]);
      setCond(null);
      setVisitorValues([]);
      setHostRequiredTypes([]);
      setScoringCorrect([]);
      setScoringPoints(1);
      form.resetFields();
      form.setFieldsValue({ widget: DEFAULT_WIDGET, required: false, protected: false, clear: '', dividerAfter: false });
    }
  }, [visible, field, form]);

  // List Hosts: load the property's Manage Hosts data (admin/owner users + display
  // labels) as the default, then seed selection/labels from the field's saved
  // hostOptions (a brand-new host field defaults to ALL hosts selected).
  useEffect(() => {
    if (!visible || widget !== 'host-picker' || !propertyId) {
      return;
    }
    setHostLoading(true);
    propertyHostService.getAdmins(propertyId).then((res) => {
      if (res && res.code === SUCCESS_CODE) {
        const rows = res.data || [];
        setHostRows(rows);
        const existing = field && Array.isArray(field.hostOptions) ? field.hostOptions : null;
        const sel = {};
        const lbls = {};
        rows.forEach((r) => {
          const match = existing ? existing.find((o) => Number(o.userId) === Number(r.user_id)) : null;
          sel[r.user_id] = existing ? !!match : true;
          lbls[r.user_id] = (match && match.label) || r.host_display || r.name || '';
        });
        setHostSelected(sel);
        setHostLabels(lbls);
      }
    }).finally(() => setHostLoading(false));
  }, [visible, widget, propertyId, field]);

  const onValuesChange = (changed) => {
    if (changed.widget !== undefined) {
      setWidget(changed.widget);
      // Drop any validation rules that don't apply to the newly chosen field type
      // (e.g. switching to a checkbox strips length/pattern/format rules).
      setRules((prev) => pruneRulesForWidget(prev, changed.widget));
      // Visitor Types is a dropdown of the visitor-type { value, label } pairs. Seed
      // all of them the first time it's chosen, then the author can remove/re-add.
      if (changed.widget === 'visitor-types') {
        if (!visitorValues.length) {
          setVisitorValues([...ALL_VISITOR_VALUES]);
        }
        // Fixed key so the mobile app / meta_data can reliably reference it.
        form.setFieldsValue({ key: 'visitor_types' });
        setKeyTouched(true);
      }
      // List Hosts uses a fixed key so the mobile app / meta_data can reference it.
      if (changed.widget === 'host-picker') {
        form.setFieldsValue({ key: 'hosts' });
        setKeyTouched(true);
      }
    }
    if (changed.label !== undefined && !keyTouched && !field) {
      form.setFieldsValue({ key: slugifyKey(changed.label) });
    }
    if (changed.key !== undefined) {
      setKeyTouched(true);
    }
  };

  // ---- conditional visibility editor ----
  const priorFieldOptions = (priorFields || []).map((f) => ({
    value: f.key,
    label: f.label ? `${f.label} (${f.key})` : f.key,
  }));
  const condSourceField = cond && cond.field
    ? (priorFields || []).find((f) => f.key === cond.field)
    : null;
  const condNeedsValue = cond && VISIBILITY_OPERATORS_WITH_VALUE.indexOf(cond.operator) !== -1;
  // A Visitor Types source field lets the condition match MULTIPLE values ("is one
  // of" / "is none of"), so its value control is a multi-select.
  const condSourceIsVisitorTypes = !!(condSourceField
    && (condSourceField.widget === 'visitor-types' || condSourceField.key === 'visitor_types'));

  const toggleCond = (enabled) => setCond(enabled ? { field: undefined, operator: 'eq', value: undefined } : null);
  const patchCond = (patch) => setCond((prev) => ({ ...(prev || {}), ...patch }));

  // The value control adapts to the controlling field: a multi-select for Visitor
  // Types (match any of several), a dropdown for any other option-based field,
  // Yes/No for a switch or single checkbox, otherwise free text.
  const renderCondValue = () => {
    if (condSourceField && Array.isArray(condSourceField.options) && condSourceField.options.length) {
      // Options may be plain strings or { value, label } objects (e.g. Visitor Types).
      const options = condSourceField.options.map((o) => {
        const value = o && typeof o === 'object' ? o.value : o;
        const label = o && typeof o === 'object' ? o.label : o;
        return { value: String(value), label: String(label) };
      });
      if (condSourceIsVisitorTypes) {
        // Normalize a legacy scalar value to an array for the multi-select.
        const multiValue = Array.isArray(cond.value)
          ? cond.value
          : (cond.value !== undefined && cond.value !== '' ? [cond.value] : []);
        return (
          <Select
            mode="multiple"
            allowClear
            style={{ minWidth: 200 }}
            placeholder="One or more values"
            value={multiValue}
            options={options}
            optionFilterProp="label"
            onChange={(value) => patchCond({ value })}
          />
        );
      }
      return (
        <Select
          style={{ width: 200 }}
          placeholder="Value"
          value={cond.value}
          options={options}
          onChange={(value) => patchCond({ value })}
        />
      );
    }
    if (condSourceField && (condSourceField.widget === 'switch' || condSourceField.widget === 'checkbox')) {
      return (
        <Select
          style={{ width: 200 }}
          placeholder="Value"
          value={cond.value}
          options={[{ value: 'true', label: 'Yes / On' }, { value: 'false', label: 'No / Off' }]}
          onChange={(value) => patchCond({ value })}
        />
      );
    }
    return (
      <Input
        style={{ width: 200 }}
        placeholder="Value"
        value={cond.value}
        onChange={(e) => patchCond({ value: e.target.value })}
      />
    );
  };

  // ---- rules editor ----
  // Only the rule kinds / format options that make sense for the current field type.
  const allowedRuleKinds = getAllowedRuleKinds(widget);
  const ruleKindOptions = RULE_KINDS.filter((k) => allowedRuleKinds.indexOf(k.value) !== -1);
  const ruleTypeOptions = getAllowedRuleTypeOptions(widget);
  const canAddRule = widgetSupportsValidationRules(widget);

  const addRule = () => setRules((prev) => [
    ...prev,
    { kind: allowedRuleKinds[0] || 'type', value: undefined, message: '' },
  ]);
  const updateRule = (index, patch) => setRules((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const removeRule = (index) => setRules((prev) => prev.filter((_, i) => i !== index));

  const renderRuleValue = (rule, index) => {
    if (rule.kind === 'type') {
      return (
        <Select
          style={{ width: 130 }}
          placeholder="Format"
          value={rule.value}
          options={ruleTypeOptions}
          onChange={(value) => updateRule(index, { value })}
        />
      );
    }
    if (rule.kind === 'pattern') {
      return (
        <Input
          style={{ width: 160 }}
          placeholder="^[0-9]+$"
          value={rule.value}
          onChange={(e) => updateRule(index, { value: e.target.value })}
        />
      );
    }
    if (NUMERIC_RULE_KINDS.indexOf(rule.kind) !== -1) {
      return (
        <InputNumber
          style={{ width: 100 }}
          placeholder="0"
          value={rule.value}
          onChange={(value) => updateRule(index, { value })}
        />
      );
    }
    return null;
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        // Visitor Types and List Hosts use fixed keys so the mobile app can find them
        // (form-level meta_data.visitor_types / hosts + question_visibility keys off them).
        let key = (values.key || '').trim();
        if (values.widget === 'visitor-types') {
          key = 'visitor_types';
        } else if (values.widget === 'host-picker') {
          key = 'hosts';
        }
        const clash = (existingKeys || []).indexOf(key) !== -1;
        if (clash) {
          message.error('Field key must be unique within the step');
          return;
        }
        const result = { key, label: values.label };
        if (values.widget && values.widget !== DEFAULT_WIDGET) {
          result.widget = values.widget;
        }
        // Static display widgets (fixed_text / fixed_image) collect no value, so they
        // can never be required or validated — never persist required/rules for them.
        const isContentWidget = WIDGETS_WITH_CONTENT.indexOf(values.widget) !== -1;
        // Protected: only a library template can carry the flag. Set from the
        // Manage Library Of Forms builder; the per-property builder never shows the
        // switch, and the backend resolves protection from the template anyway.
        if (values.protected && !isProperty) {
          result.protected = true;
        }
        if (values.required && !isContentWidget) {
          result.required = true;
        }
        if (values.placeholder) {
          result.placeholder = values.placeholder;
        }
        if (WIDGETS_WITH_OPTIONS.indexOf(values.widget) !== -1 && values.options && values.options.length) {
          result.options = values.options;
        }
        // Visitor Types: persist the selected visitor-type { value, label } pairs.
        if (values.widget === 'visitor-types') {
          const chosen = visitorOptionsFor(visitorValues);
          if (!chosen.length) {
            message.error('Select at least one visitor type');
            return;
          }
          result.options = chosen;
        }
        // Answer key. Only saved for choice widgets that actually carry options,
        // and only when the author both marked correct answers AND allocated
        // points — a half-configured key would silently drag every score down.
        const canScore = WIDGETS_WITH_OPTIONS.indexOf(values.widget) !== -1
          && Array.isArray(values.options) && values.options.length > 0;
        if (canScore && scoringCorrect.length && scoringPoints > 0) {
          result.scoring = {
            correct: scoringCorrect,
            points: scoringPoints,
          };
        }
        if (values.clear) {
          result.clear = values.clear;
        }
        // A rule across the row below this field. Only written when ON, so a
        // form that does not use it keeps the schema it had.
        if (values.dividerAfter) {
          result.dividerAfter = true;
        }
        // Only persist rules that apply to this field type — pruneRulesForWidget
        // returns [] for display/boolean/choice/file widgets, so a checkbox never
        // saves length/format rules even if some lingered in state.
        const builtRules = buildRulesFromState(pruneRulesForWidget(rules, values.widget), values.widget);
        if (builtRules.length) {
          result.rules = builtRules;
        }
        // A switch field can hide another step when turned on.
        if (values.widget === 'switch' && values.hiddenId) {
          result.hiddenId = values.hiddenId;
        }
        // Static display content (fixed_text / fixed_image).
        if (WIDGETS_WITH_CONTENT.indexOf(values.widget) !== -1 && values.content) {
          result.content = values.content;
        }
        // The document's own name, so the form shows what it is rather than a
        // storage URL ending in a hash.
        if (values.widget === 'fixed_pdf' && values.contentName) {
          result.contentName = values.contentName;
        }
        // Scale points.
        if (values.widget === 'scale' && values.max) {
          result.max = Number(values.max);
        }
        // List Hosts: persist the selected property hosts + their (edited) labels.
        // Only in the per-property builder (propertyId present); in the library
        // builder a host field carries no config and falls back to the property's
        // hosts at runtime. Requires at least one selected host.
        if (values.widget === 'host-picker' && propertyId) {
          const chosen = hostRows
            .filter((r) => hostSelected[r.user_id])
            .map((r) => ({ userId: Number(r.user_id), label: (hostLabels[r.user_id] || r.host_display || r.name || '').trim() }));
          if (!chosen.length) {
            message.error('Select at least one host for the List Hosts field');
            return;
          }
          result.hostOptions = chosen;
        }
        // List Hosts: the visitor types for which selecting a host is required
        // (only meaningful when the form has a Visitor Types field).
        if (values.widget === 'host-picker' && hostRequiredTypes.length) {
          result.requiredVisitorTypes = hostRequiredTypes;
        }
        // Conditional visibility: show this field only when an earlier field's
        // answer matches. eq/ne need a value; filled/empty do not.
        if (cond) {
          if (!cond.field) {
            message.error('Select a field for the visibility condition');
            return;
          }
          const needsValue = VISIBILITY_OPERATORS_WITH_VALUE.indexOf(cond.operator) !== -1;
          const valueEmpty = cond.value === undefined || cond.value === ''
            || (Array.isArray(cond.value) && cond.value.length === 0);
          if (needsValue && valueEmpty) {
            message.error('Enter a value for the visibility condition');
            return;
          }
          // Collapse a single-element multi-select back to a scalar for a tidy schema.
          const value = Array.isArray(cond.value) && cond.value.length === 1 ? cond.value[0] : cond.value;
          result.visibleWhen = needsValue
            ? { field: cond.field, operator: cond.operator, value }
            : { field: cond.field, operator: cond.operator };
        }
        onOk(result);
      })
      .catch(() => {});
  };

  return (
    <Modal
      title={field ? 'Edit Field' : 'Add Field'}
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Save"
      maskClosable={false}
      width={600}
    >
      <Form form={form} layout="vertical" onValuesChange={onValuesChange}>
        <Form.Item
          name="label"
          label="Label"
          rules={[{ required: true, whitespace: true, message: 'Label is required' }]}
        >
          <Input placeholder="e.g. First Name" />
        </Form.Item>

        <Form.Item
          name="key"
          label="Field key"
          rules={[{ required: true, whitespace: true, message: 'Key is required' }]}
          tooltip={
            widget === 'visitor-types' ? 'Visitor Types uses the fixed key "visitor_types" so the mobile app can recognise it.'
              : widget === 'host-picker' ? 'List Hosts uses the fixed key "hosts" so the mobile app can recognise it.'
                : undefined
          }
        >
          <Input placeholder="e.g. firstName" disabled={widget === 'visitor-types' || widget === 'host-picker'} />
        </Form.Item>

        <Form.Item name="widget" label="Type">
          <Select options={LIBRARY_FIELD_WIDGETS} />
        </Form.Item>

        <Form.Item name="placeholder" label="Placeholder">
          <Input placeholder="Optional" />
        </Form.Item>

        {widget === 'fixed_text' && (
          <Form.Item name="content" label="Text content" rules={[{ required: true, whitespace: true, message: 'Enter the text to display' }]}>
            <Input.TextArea rows={3} placeholder="Text shown on the form" />
          </Form.Item>
        )}

        {widget === 'fixed_pdf' && (
          <>
            <Form.Item
              label="PDF"
              tooltip="Upload a PDF from this computer, or pick one already in the File Manager. Either way it is stored in the File Manager and the person filling the form can view and download it."
            >
              <Space wrap>
                {/* Straight from this computer, one click. The file still
                    lands in the File Manager (the same endpoint the picker's
                    Upload tab posts to), so a document attached here is not a
                    loose copy nobody can find again. */}
                <Upload
                  accept=".pdf,application/pdf"
                  showUploadList={false}
                  maxCount={1}
                  beforeUpload={(file) => {
                    const isPdf = file.type === 'application/pdf'
                      || /\.pdf$/i.test(file.name || '');
                    if (!isPdf) {
                      message.error('That file is not a PDF');
                      return Upload.LIST_IGNORE;
                    }
                    setPdfUploading(true);
                    attachment
                      .uploadFile(file, 'file')
                      .then((resp) => {
                        const att = resp && resp.attachment;
                        const url = att && (att.full_path || att.url);
                        if (!url) {
                          message.error('Upload failed');
                          return;
                        }
                        form.setFieldsValue({
                          content: url,
                          // Same rule as the picker: never overwrite a display
                          // name the author wrote themselves.
                          contentName: form.getFieldValue('contentName') || file.name,
                        });
                        message.success('PDF uploaded');
                      })
                      .catch(() => message.error('Upload failed'))
                      .finally(() => setPdfUploading(false));
                    // Handled above — stop antd from posting it a second time.
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />} loading={pdfUploading}>
                    Upload PDF
                  </Button>
                </Upload>
                <Button icon={<FolderOpenOutlined />} onClick={() => setPdfBrowserVisible(true)}>
                  Choose from File Manager
                </Button>
                <Form.Item shouldUpdate={(prev, cur) => prev.content !== cur.content || prev.contentName !== cur.contentName} noStyle>
                  {({ getFieldValue }) => {
                    const url = getFieldValue('content');
                    return url ? (
                      <Space>
                        <FilePdfOutlined style={{ color: '#b42318' }} />
                        <a href={resolveStoredFileUrl(url)} target="_blank" rel="noopener noreferrer">
                          {getFieldValue('contentName') || 'Open'}
                        </a>
                      </Space>
                    ) : (
                      <Text type="secondary">No file chosen</Text>
                    );
                  }}
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item
              name="content"
              label="PDF URL"
              rules={[{ required: true, whitespace: true, message: 'Choose a PDF or enter its URL' }]}
            >
              <Input placeholder="https://…  (filled in when you choose a file)" />
            </Form.Item>
            <Form.Item
              name="contentName"
              label="Shown as"
              tooltip="What the document is called on the form. Filled in from the file name; change it to something the reader will recognise."
            >
              <Input placeholder="e.g. Chemical Safety Data Sheet" />
            </Form.Item>
          </>
        )}

        {widget === 'fixed_image' && (
          <>
            <Form.Item label="Image" tooltip="Upload an image file or paste an image URL below. The image is shown as-is on the form.">
              <Upload
                name="Image[file]"
                action={apiEndpoint.images}
                headers={authHeader(true)}
                accept="image/*"
                maxCount={1}
                showUploadList={false}
                onChange={(info) => {
                  if (info.file.status === 'done') {
                    const image = info.file.response && info.file.response.image;
                    const url = image && (image.full_path || image.path);
                    if (url) {
                      form.setFieldsValue({ content: url });
                      message.success('Image uploaded');
                    } else {
                      message.error('Image upload failed');
                    }
                  } else if (info.file.status === 'error') {
                    message.error('Image upload failed');
                  }
                }}
              >
                <Button icon={<UploadOutlined />}>Upload image</Button>
              </Upload>
            </Form.Item>
            <Form.Item
              name="content"
              label="Image URL"
              rules={[{ required: true, whitespace: true, message: 'Upload an image or enter an image URL' }]}
            >
              <Input placeholder="https://…  (auto-filled when you upload)" />
            </Form.Item>
            <Form.Item shouldUpdate={(prev, cur) => prev.content !== cur.content} noStyle>
              {({ getFieldValue }) => {
                const url = getFieldValue('content');
                return url ? (
                  <img src={url} alt="preview" style={{ maxWidth: '100%', maxHeight: 160, marginBottom: 12, borderRadius: 4 }} />
                ) : null;
              }}
            </Form.Item>
          </>
        )}

        {widget === 'scale' && (
          <Form.Item name="max" label="Scale points" tooltip="Number of rating points (e.g. 5).">
            <InputNumber min={2} max={10} placeholder="5" />
          </Form.Item>
        )}

        {WIDGETS_WITH_OPTIONS.indexOf(widget) !== -1 && (
          <Form.Item
            name="options"
            label="Options"
            tooltip="Type an option and press Enter. Required for dropdown / checkbox group / radio group."
            rules={[{ required: true, type: 'array', min: 1, message: 'Add at least one option' }]}
          >
            {/* No tokenSeparators: commas are valid inside an option (e.g. "No oil,
                fuel, coolant leaks"). Each option is added by pressing Enter. */}
            <Select mode="tags" placeholder="Add options..." />
          </Form.Item>
        )}

        {/* Answer key — the block that turns a question into a graded one. Shown
            only once options exist, because there is nothing to mark against
            before that. Leaving it empty keeps the question ungraded, which is
            a perfectly normal thing for a comments box on an assessment. */}
        {WIDGETS_WITH_OPTIONS.indexOf(widget) !== -1 && (
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.options !== cur.options}
          >
            {({ getFieldValue }) => {
              const opts = getFieldValue('options') || [];
              if (!opts.length) {
                return null;
              }
              return (
                <div style={{ padding: '12px 14px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, marginBottom: 16 }}>
                  <Text strong>Marking (training assessments)</Text>
                  <div style={{ color: '#8c8c8c', fontSize: 12, margin: '2px 0 10px' }}>
                    Mark the correct answer(s) to have this question scored automatically when
                    it is used as a training assessment. Leave empty to leave it ungraded.
                    {widget === 'checkbox-group' && ' A checkbox group must be answered exactly — every correct option and nothing else.'}
                  </div>
                  <Form.Item label="Correct answer(s)" style={{ marginBottom: 10 }}>
                    <Select
                      mode="multiple"
                      allowClear
                      value={scoringCorrect}
                      onChange={setScoringCorrect}
                      placeholder="Not graded"
                      options={opts.map((o) => ({ value: String(o), label: String(o) }))}
                    />
                  </Form.Item>
                  <Form.Item label="Points" style={{ marginBottom: 0 }}>
                    <InputNumber
                      min={1}
                      max={100}
                      value={scoringPoints}
                      onChange={(v) => setScoringPoints(v || 1)}
                      disabled={!scoringCorrect.length}
                    />
                  </Form.Item>
                </div>
              );
            }}
          </Form.Item>
        )}

        {widget === 'visitor-types' && (
          <Form.Item
            label="Visitor types shown"
            tooltip="Select the visitor types to include in the dropdown. Options come from the visitorTypes constant (shows the label, submits the value). Remove one to drop it; select again to re-add."
            extra={(
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() => setVisitorValues([...ALL_VISITOR_VALUES])}
              >
                Select all
              </Button>
            )}
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Select visitor types..."
              value={visitorValues}
              onChange={setVisitorValues}
              options={VISITOR_TYPE_SELECT_OPTIONS}
              optionFilterProp="label"
            />
          </Form.Item>
        )}

        {widget === 'host-picker' && (
          <Form.Item label="Hosts" tooltip="Choose which of the property's hosts appear in this list and set the label each host shows to visitors. Loaded from Manage Hosts.">
            {!propertyId ? (
              <Text type="secondary">
                Hosts are configured per property. Add this field to a property form to choose which hosts appear and edit their labels; otherwise it shows all of the property&apos;s hosts.
              </Text>
            ) : (
              <Table
                rowKey="user_id"
                size="small"
                loading={hostLoading}
                dataSource={hostRows}
                pagination={false}
                locale={{ emptyText: 'No admin/owner users on this property.' }}
                columns={[
                  {
                    title: 'Show',
                    key: 'show',
                    width: 60,
                    render: (_, row) => (
                      <Checkbox
                        checked={!!hostSelected[row.user_id]}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setHostSelected((prev) => ({ ...prev, [row.user_id]: checked }));
                        }}
                      />
                    ),
                  },
                  {
                    title: 'User',
                    key: 'user',
                    render: (_, row) => (
                      <div>
                        <div>
                          {row.name} {row.role && <Tag color={row.role === 'owner' ? 'gold' : 'blue'}>{row.role}</Tag>}
                        </div>
                        <div style={{ color: '#999', fontSize: 12 }}>{row.email}</div>
                      </div>
                    ),
                  },
                  {
                    title: 'Label shown to visitors',
                    key: 'label',
                    width: 240,
                    render: (_, row) => (
                      <Input
                        placeholder={row.host_display || row.name}
                        value={hostLabels[row.user_id]}
                        disabled={!hostSelected[row.user_id]}
                        onChange={(e) => {
                          const value = e.target.value;
                          setHostLabels((prev) => ({ ...prev, [row.user_id]: value }));
                        }}
                        allowClear
                      />
                    ),
                  },
                ]}
              />
            )}
          </Form.Item>
        )}

        {widget === 'host-picker' && Array.isArray(visitorTypeOptions) && visitorTypeOptions.length > 0 && (
          <Form.Item
            label="Required for visitor types"
            tooltip="Selecting a host is required for these visitor types. Options come from this form's Visitor Types field. Leave empty to make host selection optional for everyone."
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Host required for..."
              value={hostRequiredTypes}
              onChange={setHostRequiredTypes}
              options={visitorTypeOptions}
              optionFilterProp="label"
            />
          </Form.Item>
        )}

        <Form.Item
          name="clear"
          label="Column clear"
          tooltip="In a 2-column step, force the rest of the row empty so the next field starts on a new row."
        >
          <Select options={FIELD_CLEAR_OPTIONS} />
        </Form.Item>

        {WIDGETS_WITH_CONTENT.indexOf(widget) === -1 && (
          <Form.Item name="required" label="Required" valuePropName="checked">
            <Switch />
          </Form.Item>
        )}

        {/* Available on EVERY widget, display blocks included — closing a
            section off under a Fixed Text heading is as reasonable as under a
            question. */}
        <Form.Item
          name="dividerAfter"
          label="Add horizontal line at end"
          valuePropName="checked"
          tooltip="Draw a line across the row below this field, to separate it from what follows."
        >
          <Switch />
        </Form.Item>

        {!isProperty && (
          <Form.Item
            name="protected"
            label="Protected"
            valuePropName="checked"
            tooltip="Lock this field for properties that adopt this template: they cannot edit or delete it in their standalone copy. They can still reorder it and add their own fields around it. Takes effect immediately on properties that already adopted this form."
          >
            <Switch />
          </Form.Item>
        )}

        {widget === 'switch' && (
          <Form.Item
            name="hiddenId"
            label="Hide step when ON"
            tooltip="When this switch is turned ON, the selected step is hidden/skipped in the form (and shown again when OFF). Leave empty for no effect."
          >
            <Select allowClear placeholder="Select a step to hide" options={stepOptions || []} />
          </Form.Item>
        )}

        {canAddRule && (
        <div>
          <Text strong>Validation rules</Text>
          <div style={{ marginTop: 4, marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Only the rules that apply to a “{(LIBRARY_FIELD_WIDGETS.find((w) => w.value === widget) || {}).label || widget}” field are shown. Use the Required switch above for a mandatory answer.
            </Text>
          </div>
          <div style={{ marginTop: 8 }}>
            {rules.map((rule, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Space key={index} align="baseline" style={{ display: 'flex', marginBottom: 8 }} wrap>
                <Select
                  style={{ width: 150 }}
                  value={rule.kind}
                  options={ruleKindOptions}
                  onChange={(kind) => updateRule(index, { kind, value: undefined })}
                />
                {renderRuleValue(rule, index)}
                <Input
                  style={{ width: 180 }}
                  placeholder="Error message"
                  value={rule.message}
                  onChange={(e) => updateRule(index, { message: e.target.value })}
                />
                <MinusCircleOutlined onClick={() => removeRule(index)} />
              </Space>
            ))}
            <Button type="dashed" icon={<PlusOutlined />} onClick={addRule} block>
              Add rule
            </Button>
          </div>
        </div>
        )}

        <div style={{ marginTop: 16 }}>
          <Text strong>Conditional visibility</Text>
          <div style={{ marginTop: 8 }}>
            <Space align="baseline" style={{ marginBottom: cond ? 8 : 0 }}>
              <Switch checked={!!cond} onChange={toggleCond} disabled={!priorFieldOptions.length} />
              <Text type="secondary">
                {priorFieldOptions.length
                  ? 'Only show this field when an earlier field matches a condition'
                  : 'Add a field before this one to make it conditional'}
              </Text>
            </Space>
            {cond && (
              <Space align="baseline" wrap style={{ display: 'flex' }}>
                <Select
                  style={{ width: 200 }}
                  placeholder="Select a field"
                  value={cond.field}
                  options={priorFieldOptions}
                  onChange={(field) => patchCond({ field, value: undefined })}
                  showSearch
                  optionFilterProp="label"
                />
                <Select
                  style={{ width: 130 }}
                  value={cond.operator}
                  options={VISIBILITY_OPERATORS}
                  onChange={(operator) => patchCond({ operator })}
                />
                {condNeedsValue && renderCondValue()}
              </Space>
            )}
          </div>
        </div>
      </Form>

      {/* The File Manager picker: its Library tab is the File Manager, its
          Upload tab takes a file from this computer and selects it straight
          away. Rendered outside the Form so its own controls never submit it.
          `propertyId` is absent in the library builder, where the picker falls
          back to what that user can see.

          Mounted only while open: there is nothing for it to do closed, and
          this editor is on screen far more often than the picker is. */}
      {pdfBrowserVisible && (
      <FileBrowser
        visible={pdfBrowserVisible}
        onCancel={() => setPdfBrowserVisible(false)}
        accept="pdf"
        maxCount={1}
        title="Choose a PDF"
        propertyId={propertyId || null}
        onSelect={(files) => {
          const file = Array.isArray(files) ? files[0] : files;
          const url = file && (file.url || file.full_path || file.path);
          if (!url) {
            message.error('That file has no address to link to');
            return;
          }
          form.setFieldsValue({
            content: url,
            // Only fill the display name if the author has not written their
            // own — re-picking a file should not overwrite "Chemical Safety
            // Data Sheet" with "SDS_v3_final.pdf".
            contentName: form.getFieldValue('contentName') || getFileName(file),
          });
          setPdfBrowserVisible(false);
        }}
      />
      )}
    </Modal>
  );
};

export default FieldEditorModal;
